import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Briefcase, Heart, Sparkles, Lock, Globe, ArrowRight, Loader2 } from "lucide-react";
import PhotoDrop from "@/components/PhotoDrop";

const TYPES = [
  { key: "work",      label: "Work",      icon: Briefcase, blurb: "For teams, collaborators, professional circles." },
  { key: "friends",   label: "Friends",   icon: Heart,     blurb: "For close circles — private by default." },
  { key: "community", label: "Community", icon: Sparkles,  blurb: "For open, mission-driven groups." },
];

export default function GroupCreate() {
  const nav = useNavigate();
  const [f, setF] = useState({
    name: "", description: "", type: "community", privacy: "public",
    avatar: "", cover: "", price_monthly: 0,
  });
  const [busy, setBusy] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    if (!f.name.trim()) { toast.error("Give your group a name"); return; }
    setBusy(true);
    try {
      const payload = { ...f, price_monthly: Number(f.price_monthly || 0) };
      const { data } = await api.post("/groups", payload);
      toast.success(`“${data.name}” is live.`);
      nav(`/groups/${data.id}`);
    } catch (e2) { toast.error(formatErr(e2)); }
    finally { setBusy(false); }
  };

  const priceOk = f.privacy === "private" || Number(f.price_monthly || 0) === 0;

  return (
    <form onSubmit={save} className="max-w-2xl mx-auto px-4 sm:px-6 py-10 space-y-6" data-testid="group-create-form">
      <div>
        <p className="overline">New circle</p>
        <h1 className="h-display text-4xl mt-1">Spin up a group.</h1>
        <p className="text-[#4A4A4A] mt-1">Work · Friends · Community. Private groups can be free or paid.</p>
      </div>

      <div className="card p-6 space-y-5">
        <div>
          <label className="text-sm font-medium">Name</label>
          <input className="input mt-1.5" value={f.name} onChange={(e)=>setF({...f,name:e.target.value})} placeholder="Lisbon Creatives" data-testid="group-name-input"/>
        </div>
        <div>
          <label className="text-sm font-medium">Description</label>
          <textarea rows={3} className="input mt-1.5 resize-none" value={f.description} onChange={(e)=>setF({...f,description:e.target.value})} placeholder="What is this group about?" data-testid="group-desc-input"/>
        </div>

        <div>
          <p className="text-sm font-medium mb-2">Type</p>
          <div className="grid sm:grid-cols-3 gap-2">
            {TYPES.map(t => (
              <button key={t.key} type="button"
                onClick={()=>setF({...f, type:t.key})}
                data-testid={`group-type-${t.key}`}
                className={`text-left rounded-2xl border p-3 transition ${f.type===t.key ? "border-[#0A0A0A] bg-[#FAFAF8]" : "border-[#E5E5E3] hover:border-[#0A0A0A]"}`}>
                <t.icon size={16} className="text-[#0057FF]"/>
                <p className="font-medium text-sm mt-1.5">{t.label}</p>
                <p className="text-[11px] text-[#8A8A8A]">{t.blurb}</p>
              </button>
            ))}
          </div>
        </div>

        <div>
          <p className="text-sm font-medium mb-2">Privacy</p>
          <div className="grid sm:grid-cols-2 gap-2">
            <button type="button" onClick={()=>setF({...f, privacy:"public"})}
              data-testid="group-privacy-public"
              className={`text-left rounded-2xl border p-3 transition ${f.privacy==="public" ? "border-[#0A0A0A] bg-[#FAFAF8]" : "border-[#E5E5E3] hover:border-[#0A0A0A]"}`}>
              <Globe size={16} className="text-[#0057FF]"/>
              <p className="font-medium text-sm mt-1.5">Public</p>
              <p className="text-[11px] text-[#8A8A8A]">Anyone on Hoomanz can join instantly.</p>
            </button>
            <button type="button" onClick={()=>setF({...f, privacy:"private", price_monthly: f.price_monthly || 0})}
              data-testid="group-privacy-private"
              className={`text-left rounded-2xl border p-3 transition ${f.privacy==="private" ? "border-[#0A0A0A] bg-[#FAFAF8]" : "border-[#E5E5E3] hover:border-[#0A0A0A]"}`}>
              <Lock size={16} className="text-[#0057FF]"/>
              <p className="font-medium text-sm mt-1.5">Private</p>
              <p className="text-[11px] text-[#8A8A8A]">Invite only, or approve join requests. Can be paid.</p>
            </button>
          </div>
        </div>

        {f.privacy === "private" && (
          <div className="rounded-2xl border border-[#E5E5E3] bg-[#FAFAF8] p-4" data-testid="group-paid-block">
            <p className="text-xs uppercase tracking-wider text-[#4A4A4A] mb-2">Membership</p>
            <label className="text-sm font-medium">Monthly price (USD)</label>
            <div className="flex items-center gap-2 mt-1.5">
              <span className="text-[#8A8A8A]">$</span>
              <input type="number" min="0" step="1" className="input max-w-[140px]"
                value={f.price_monthly}
                onChange={(e)=>setF({...f, price_monthly:e.target.value})}
                placeholder="0 = free" data-testid="group-price-input"/>
              <span className="text-xs text-[#8A8A8A]">/ month</span>
            </div>
            <p className="text-[11px] text-[#8A8A8A] mt-2">
              Free: members join by invite or approved request. Paid: Stripe monthly subscription — you keep 95%, Hoomanz keeps 5%.
              {Number(f.price_monthly || 0) > 0 && " · Requires Stripe Connect payouts to be set up."}
            </p>
          </div>
        )}

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <p className="text-sm font-medium mb-1.5">Cover photo</p>
            <PhotoDrop value={f.cover} onChange={(v)=>setF({...f, cover:v})} aspect="cover" label="Cover" testid="group-cover"/>
          </div>
          <div>
            <p className="text-sm font-medium mb-1.5">Icon / avatar</p>
            <PhotoDrop value={f.avatar} onChange={(v)=>setF({...f, avatar:v})} aspect="square" label="Icon" testid="group-avatar"/>
          </div>
        </div>
      </div>

      <button type="submit" disabled={busy || !priceOk} className="btn-primary w-full" data-testid="group-create-submit">
        {busy ? <Loader2 size={14} className="animate-spin"/> : <>Create group <ArrowRight size={14}/></>}
      </button>
    </form>
  );
}
